<script setup lang="ts">
import { computed } from 'vue';
import { TableGridItem, Item } from '@/models/table-grid-interface';
import { useLocalization } from '@/composables';
import { shortenCutEnd } from '@/utils/utils';
import MemberAvatar from '../base/MemberAvatar.vue';
import emptyAvatar from '@/assets/images/avatar_undefined.png';

const { lt } = useLocalization('uiRevolverMatrix');

const props = withDefaults(defineProps<{
  user: TableGridItem | null;
  size?: 'large' | 'medium' | 'small';
  titleField: string;
  subtitleField: string;
  titleFieldMax: number;
  subtitleFieldMax: number;
  cardBackgroundColor: string;
  emptySlotColor: string;
  showArrow?: boolean;
  backgroundColor: string;
  // New props
  cardClass?: string;
  emptyCardClass?: string;
  cardTitleColor?: string;
  cardSubtitleColor?: string;
  avatarSize?: number;
  showEmptyIcon?: boolean;
  emptyText?: string;
  arrowColor?: string;
}>(), {
  size: 'medium',
  showArrow: false,
  backgroundColor: '#ffffff',
  cardClass: '',
  emptyCardClass: '',
  cardTitleColor: '#000000',
  cardSubtitleColor: '#757575',
  avatarSize: undefined,
  showEmptyIcon: true,
  emptyText: '',
  arrowColor: '#ffffff',
});

const emit = defineEmits<{
  (e: 'click', user: TableGridItem | null): void;
}>();

const computedAvatarSize = computed(() => {
  if (props.avatarSize !== undefined) return props.avatarSize;
  switch (props.size) {
    case 'large': return 60;
    case 'small': return 40;
    default: return 50;
  }
});

const cardPadding = computed(() => {
  switch (props.size) {
    case 'large': return 'pa-3';
    case 'small': return 'pa-2';
    default: return 'pa-3';
  }
});

// Always white background, even for empty slots
const computedCardBackgroundColor = computed(() => props.cardBackgroundColor);

// Compute classes for the card
const cardClasses = computed(() => {
  const classes = ['d-flex', 'flex-column', 'align-center', 'rounded-lg', cardPadding.value];
  if (props.user) {
    if (props.cardClass) classes.push(props.cardClass);
  } else {
    if (props.emptyCardClass) classes.push(props.emptyCardClass);
  }
  return classes;
});

const title = computed(() => {
  if (!props.user) return '';
  const value = props.user[props.titleField]?.presentable || props.user[props.titleField]?.raw || '';
  return shortenCutEnd(String(value), props.titleFieldMax);
});

const subtitle = computed(() => {
  if (!props.user) return '';
  const value = props.user[props.subtitleField]?.presentable || props.user[props.subtitleField]?.raw || '';
  return shortenCutEnd(String(value), props.subtitleFieldMax);
});

const avatarUrl = computed(() => {
  if (!props.user) return emptyAvatar;
  return Item(props.user).avatarUrl();
});

const emptySlotText = computed(() => {
  return props.emptyText || lt('empty_slot');
});
</script>

<template>
  <div
    :class="cardClasses"
    :style="{
      backgroundColor: computedCardBackgroundColor,
      cursor: 'pointer',
      position: 'relative',
      paddingBottom: showArrow ? '24px' : undefined
    }"
    @click="emit('click', user)"
  >
    <template v-if="user">
      <div
        v-if="size !== 'large'"
        class="text-subtitle-2 font-weight-bold text-truncate"
        :style="{ maxWidth: '100%', color: cardTitleColor }"
      >
        {{ title }}
      </div>
      <div v-else class="text-body-1 font-weight-medium px-4 py-2 text-truncate" :style="{ color: cardTitleColor }">
        {{ title }}
      </div>
      <div class="text-caption mb-1" :style="{ color: cardSubtitleColor }">
        {{ subtitle }}
      </div>
    </template>
    <MemberAvatar
      v-if="user || showEmptyIcon"
      :src="avatarUrl"
      :size="computedAvatarSize"
      :title="title"
    />

    <div v-if="!user" class="text-caption mt-2 text-center" :style="{ color: cardSubtitleColor }">
      {{ emptySlotText }}
    </div>

    <!-- Arrow icon for non-bottom levels -->
    <v-icon
      v-if="showArrow"
      icon="mdi-menu-down"
      size="40"
      :color="arrowColor"
      :style="{
        position: 'absolute',
        bottom: '-23px',
        left: '50%',
        transform: 'translateX(-50%)'
      }"
    />
  </div>
</template>
